import { z } from "zod";

export const ANALYTICS_EVENT_TYPES = [
  "menu_view",
  "category_view",
  "item_view",
  "language_change",
  "add_to_cart",
  "checkout_started",
  "order_submitted",
] as const;
export const analyticsEventTypeSchema = z.enum(ANALYTICS_EVENT_TYPES);
export type AnalyticsEventType = z.infer<typeof analyticsEventTypeSchema>;

export const analyticsBatchSchema = z.object({
  menuId: z.string().min(1).max(36),
  /** Random per-tab session id generated client side (not a persistent identifier). */
  sessionId: z.string().min(8).max(64).regex(/^[A-Za-z0-9_-]+$/),
  source: z.string().max(40).regex(/^[a-z0-9_-]*$/).optional(),
  tableToken: z.string().max(64).optional(),
  events: z
    .array(
      z.object({
        type: analyticsEventTypeSchema,
        at: z.number().int().optional(),
        language: z.string().max(16).optional(),
        categoryId: z.string().max(36).optional(),
        itemId: z.string().max(36).optional(),
      }),
    )
    .min(1)
    .max(50),
});
export type AnalyticsBatch = z.infer<typeof analyticsBatchSchema>;

export type AnalyticsDailyPoint = {
  date: string;
  views: number;
  sessions: number;
  visitors: number;
  categoryViews: number;
  itemViews: number;
  languageChanges: number;
  addToCart: number;
  checkoutStarted: number;
  ordersSubmitted: number;
  orders: number;
  revenueMinor: number;
};

export type AnalyticsResponse = {
  tier: "basic" | "detailed" | "advanced";
  range: { from: string; to: string };
  totals: { views: number; sessions?: number; visitors?: number; orders?: number; revenueMinor?: number; averageOrderMinor?: number };
  daily: Array<Partial<AnalyticsDailyPoint> & { date: string; views: number }>;
  languages?: Array<{ key: string; count: number }>;
  sources?: Array<{ key: string; count: number }>;
  topItems?: Array<{ key: string; label: string; count: number }>;
  topCategories?: Array<{ key: string; label: string; count: number }>;
  funnel?: Array<{ step: string; count: number }>;
  popularProducts?: Array<{ itemId: string | null; name: string; quantity: number; revenueMinor: number }>;
  hourly?: Array<{ hour: number; orders: number }>;
  tables?: Array<{ tableId: string | null; label: string; orders: number; revenueMinor: number }>;
  currency?: string;
  lockedFeatures: string[];
};
